import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Stack,
  Button,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Grid,
  Avatar,
  Container,
  Tooltip,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  NotificationsActive as BellIcon,
  NotificationsOff as BellOffIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  AllInclusive as AllInclusiveIcon,
} from '@mui/icons-material';
import AlertRuleDialog from './components/AlertRuleDialog';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/';

const AlertManagement = () => {
  const [alertRules, setAlertRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [bulkStrategies, setBulkStrategies] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadAlertRules();
    loadBulkStrategies();
  }, []);

  const loadAlertRules = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/strategies/api/alerts/rules/`);
      setAlertRules(response.data.rules || []);
      setError(null);
    } catch (err) {
      console.error('[ALERTS] Error loading rules:', err);
      setError('Failed to load alert rules');
    } finally {
      setLoading(false);
    }
  };

  const loadBulkStrategies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/strategies/api/bulk-strategy/list/`);
      if (response.data.success) {
        setBulkStrategies(response.data.bulk_strategies || []);
      }
    } catch (err) {
      console.error('[ALERTS] Error loading strategies:', err);
    }
  };

  const handleCreate = () => {
    setEditingRule(null);
    setDialogOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setDialogOpen(true);
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this alert rule?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/strategies/api/alerts/rules/${ruleId}/delete/`);
      await loadAlertRules();
      setError(null);
    } catch (err) {
      console.error('[ALERTS] Error deleting rule:', err);
      setError('Failed to delete alert rule');
    }
  };

  const handleToggleEnabled = async (rule) => {
    try {
      await axios.put(`${API_BASE_URL}/strategies/api/alerts/rules/${rule.rule_id}/update/`, {
        is_enabled: !rule.is_enabled,
      });
      await loadAlertRules();
      setError(null);
    } catch (err) {
      console.error('[ALERTS] Error toggling rule:', err);
      setError('Failed to update alert rule');
    }
  };

  const activeRulesCount = alertRules.filter((r) => r.is_enabled).length;
  const timeRestrictedCount = alertRules.filter((r) => r.active_time_enabled).length;
  const alwaysActiveCount = alertRules.filter((r) => !r.active_time_enabled).length;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0f1419',
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255, 193, 7, 0.03) 0%, transparent 50%)',
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3.5}>
          {/* Header */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: alpha('#ffc107', 0.2),
                    border: `1px solid ${alpha('#ffc107', 0.3)}`,
                  }}
                >
                  <BellIcon sx={{ fontSize: 28, color: '#ffc107' }} />
                </Avatar>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '28px',
                      fontWeight: 700,
                      color: '#fff',
                      letterSpacing: '-0.5px',
                      mb: 0.5,
                    }}
                  >
                    Alert Management
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontWeight: 500,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    100% Completion Alerts • Current Time: {currentTime.toLocaleTimeString()}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={loadAlertRules}
                  disabled={loading}
                  sx={{
                    bgcolor: 'rgba(118, 75, 162, 0.8)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '12px',
                    textTransform: 'none',
                    borderRadius: 0.75,
                    px: 2.5,
                    '&:hover': {
                      bgcolor: 'rgba(118, 75, 162, 1)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Refresh'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreate}
                  sx={{
                    bgcolor: '#ffc107',
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '12px',
                    textTransform: 'none',
                    borderRadius: 0.75,
                    px: 2.5,
                    '&:hover': {
                      opacity: 0.85,
                    },
                  }}
                >
                  Create Alert
                </Button>
              </Stack>
            </Stack>
          </Box>

          {/* Stats */}
          <Paper
            sx={{
              bgcolor: 'rgba(255, 193, 7, 0.04)',
              border: '1px solid rgba(255, 193, 7, 0.15)',
              borderRadius: 1.5,
              p: 2.5,
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <Stack spacing={1}>
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                    }}
                  >
                    Total Alerts
                  </Typography>
                  <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#ffc107' }}>
                    {alertRules.length}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={4}>
                <Stack spacing={1}>
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                    }}
                  >
                    Active
                  </Typography>
                  <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#00ff88' }}>
                    {activeRulesCount}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={4}>
                <Stack spacing={1}>
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                    }}
                  >
                    Time-Restricted
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '24px',
                      fontWeight: 700,
                      color: timeRestrictedCount > 0 ? '#764ba2' : 'rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    {timeRestrictedCount}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Error Message */}
          {error && (
            <Paper
              sx={{
                p: 2,
                bgcolor: alpha('#ff4757', 0.1),
                border: `1px solid ${alpha('#ff4757', 0.3)}`,
                borderRadius: 1.5,
              }}
            >
              <Typography sx={{ color: '#ff4757', fontSize: '14px' }}>{error}</Typography>
            </Paper>
          )}

          {/* Alert Rules List */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#ffc107' }} />
            </Box>
          ) : alertRules.length === 0 ? (
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                bgcolor: alpha('#1a1f2e', 0.6),
                border: `1px dashed ${alpha('#ffc107', 0.2)}`,
                borderRadius: 2,
              }}
            >
              <BellIcon sx={{ fontSize: 64, color: alpha('#ffc107', 0.2), mb: 2 }} />
              <Typography variant="h6" sx={{ color: alpha('#fff', 0.4), mb: 1 }}>
                No Alert Rules
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.3), mb: 3 }}>
                Create your first alert for 100% bulk strategy completion
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                sx={{
                  bgcolor: '#ffc107',
                  color: '#000',
                  fontWeight: 700,
                  '&:hover': { opacity: 0.85 },
                }}
              >
                Create Alert Rule
              </Button>
            </Paper>
          ) : (
            <List>
              {alertRules.map((rule) => (
                <Paper
                  key={rule.rule_id}
                  sx={{
                    mb: 2,
                    bgcolor: alpha('#1e1e1e', 0.8),
                    border: `1px solid ${alpha(
                      rule.is_enabled ? '#00ff88' : '#666',
                      0.2
                    )}`,
                    borderRadius: 1.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha('#1e1e1e', 1),
                      borderColor: alpha(rule.is_enabled ? '#00ff88' : '#666', 0.4),
                    },
                  }}
                >
                  <ListItem sx={{ py: 2.5, px: 3 }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                            {rule.name}
                          </Typography>
                          
                          {/* Status Badge */}
                          {rule.is_enabled ? (
                            <Chip
                              icon={<BellIcon sx={{ fontSize: 14 }} />}
                              label="Active"
                              size="small"
                              sx={{
                                bgcolor: alpha('#00ff88', 0.15),
                                color: '#00ff88',
                                fontWeight: 600,
                                height: 24,
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<BellOffIcon sx={{ fontSize: 14 }} />}
                              label="Disabled"
                              size="small"
                              sx={{
                                bgcolor: alpha('#666', 0.15),
                                color: '#666',
                                fontWeight: 600,
                                height: 24,
                              }}
                            />
                          )}
                          
                          {/* Time Restriction Badge */}
                          {rule.active_time_enabled ? (
                            <Chip
                              icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                              label={`${rule.active_start_time} - ${rule.active_end_time}`}
                              size="small"
                              sx={{
                                bgcolor: alpha('#764ba2', 0.15),
                                color: '#764ba2',
                                fontWeight: 600,
                                height: 24,
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<AllInclusiveIcon sx={{ fontSize: 14 }} />}
                              label="24/7"
                              size="small"
                              sx={{
                                bgcolor: alpha('#00d4ff', 0.15),
                                color: '#00d4ff',
                                fontWeight: 600,
                                height: 24,
                              }}
                            />
                          )}
                          
                          {/* Currently Active Indicator */}
                          {rule.is_enabled && rule.is_currently_active && (
                            <Chip
                              label="LIVE NOW"
                              size="small"
                              sx={{
                                bgcolor: alpha('#ff4757', 0.2),
                                color: '#ff4757',
                                fontWeight: 700,
                                height: 24,
                                animation: 'pulse 2s infinite',
                              }}
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                          <Stack direction="row" spacing={2} flexWrap="wrap">
                            {rule.bulk_strategy_id && (
                              <Chip
                                label={`Strategy: ${rule.bulk_strategy_id}`}
                                size="small"
                                sx={{ bgcolor: alpha('#00d4ff', 0.1), color: '#00d4ff' }}
                              />
                            )}
                            {rule.play_sound && (
                              <Chip
                                label="🔊 Sound Enabled"
                                size="small"
                                sx={{ bgcolor: alpha('#ffc107', 0.1), color: '#ffc107' }}
                              />
                            )}
                          </Stack>

                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
                            Triggers when <strong>{rule.bulk_strategy_id}</strong> reaches 100% completion
                          </Typography>
                        </Stack>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title={rule.is_enabled ? 'Disable' : 'Enable'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleEnabled(rule)}
                            sx={{
                              bgcolor: alpha(rule.is_enabled ? '#00ff88' : '#666', 0.1),
                              '&:hover': {
                                bgcolor: alpha(rule.is_enabled ? '#00ff88' : '#666', 0.2),
                              },
                            }}
                          >
                            {rule.is_enabled ? (
                              <BellOffIcon sx={{ fontSize: 18, color: '#00ff88' }} />
                            ) : (
                              <BellIcon sx={{ fontSize: 18, color: '#666' }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(rule)}
                            sx={{
                              bgcolor: alpha('#764ba2', 0.1),
                              '&:hover': {
                                bgcolor: alpha('#764ba2', 0.2),
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18, color: '#764ba2' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(rule.rule_id)}
                            sx={{
                              bgcolor: alpha('#ff4757', 0.1),
                              '&:hover': {
                                bgcolor: alpha('#ff4757', 0.2),
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18, color: '#ff4757' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Paper>
              ))}
            </List>
          )}
        </Stack>
      </Container>

      {/* Alert Rule Dialog */}
      <AlertRuleDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingRule(null);
        }}
        editingRule={editingRule}
        bulkStrategies={bulkStrategies}
        onSave={loadAlertRules}
      />

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default AlertManagement;
